import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Instrument } from "../data/instruments";
import {
  defaultLocale,
  getLocaleConfig,
  getUiText,
  instrumentCopyByLocale,
  localizePath,
  type LocaleCode,
  type LocaleConfig,
  type InstrumentId,
  type LocalizedInstrumentCopy,
  type UiText
} from "./locales";

interface LocalizedInstrumentText {
  name: string;
  family: string;
  origin: string;
  shortDescription: string;
  culturalNote: string;
  culturalCaution?: string;
}

interface LocaleContextValue {
  locale: LocaleCode;
  config: LocaleConfig;
  t: UiText;
  localize: (pathname: string) => string;
  getInstrumentText: (instrument: Instrument) => LocalizedInstrumentText;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function mergeInstrumentText(instrument: Instrument, copy?: LocalizedInstrumentCopy): LocalizedInstrumentText {
  return {
    name: instrument.name,
    family: copy?.family ?? instrument.family,
    origin: copy?.origin ?? instrument.origin,
    shortDescription: copy?.shortDescription ?? instrument.shortDescription,
    culturalNote: copy?.culturalNote ?? instrument.culturalNote,
    culturalCaution: copy?.culturalCaution ?? instrument.culturalCaution
  };
}

export function LocaleProvider({ locale, children }: { locale: LocaleCode; children: ReactNode }) {
  const value = useMemo<LocaleContextValue>(() => {
    const config = getLocaleConfig(locale);
    const t = getUiText(locale);
    const localizedCopies = instrumentCopyByLocale[locale] ?? {};

    return {
      locale,
      config,
      t,
      localize: (pathname) => localizePath(locale, pathname),
      getInstrumentText: (instrument) => mergeInstrumentText(instrument, localizedCopies[instrument.id as InstrumentId])
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n(): LocaleContextValue {
  const value = useContext(LocaleContext);

  if (!value) {
    const config = getLocaleConfig(defaultLocale);

    return {
      locale: defaultLocale,
      config,
      t: getUiText(defaultLocale),
      localize: (pathname) => localizePath(defaultLocale, pathname),
      getInstrumentText: (instrument) => mergeInstrumentText(instrument)
    };
  }

  return value;
}
